<template>
  <div class="report-actions" v-if="hasActions">
    <dropdown-menu-button
      :disabled="menuDisabled"
      :loading="loading"
      icon-color="light"
      :dark="false"
      :icon-button="false"
      :fab-button="false"
      size="default"
      buttons-class="no-min-width"
    >
      <v-list>
        <v-list-item
          v-for="(action, key) in actions"
          :key="key"
          @click="handleAction(key, action)"
        >
          <v-list-item-title :class="{ 'red--text': action.destructive }">
            {{ action.label }} ({{ selectedCount }})
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </dropdown-menu-button>

    <!-- Action form/confirm dialog -->
    <v-dialog v-model="actionDialog" max-width="480px" :retain-focus="false">
      <modal-card
        :title="activeAction.label || 'Action'"
        @close="actionDialog = false"
      >
        <div class="pa-6">
          <p v-if="activeAction.confirm_text" class="text--secondary mb-4">
            {{ activeAction.confirm_text }}
          </p>

          <template v-if="activeActionFields">
            <div
              v-for="(field, fieldKey) in activeActionFields"
              :key="fieldKey"
              class="mb-3"
            >
              <v-select
                v-if="field.type === 'select'"
                v-model="fieldValues[fieldKey]"
                :items="field.options || []"
                :label="field.label"
                :placeholder="field.placeholder"
                class="background-dark"
              />
              <v-text-field
                v-else
                v-model="fieldValues[fieldKey]"
                :label="field.label"
                :placeholder="field.placeholder"
                class="background-dark"
              />
            </div>
          </template>
        </div>

        <template v-slot:actions>
          <v-btn
            color="appMain"
            :disabled="!actionFieldsValid"
            :loading="loading"
            @click="executeAction"
          >
            {{ activeAction.label }}
          </v-btn>
        </template>
      </modal-card>
    </v-dialog>

    <!-- Result dialog -->
    <v-dialog v-model="resultDialog" max-width="480px" :retain-focus="false">
      <modal-card
        :title="actionResult.success ? 'Action Complete' : 'Action Failed'"
        @close="resultDialog = false"
        :full="!actionResult.url"
      >
        <div class="pa-6 pt-8 pb-8 text-center">
          <v-icon
            :color="actionResult.success ? 'success' : 'error'"
            size="48"
            class="mb-4"
          >
            {{ actionResult.success ? 'check_circle' : 'error' }}
          </v-icon>
          <p class="text--secondary mb-0">{{ actionResult.message }}</p>
        </div>

        <template v-if="actionResult.url" v-slot:actions>
          <v-btn color="appMain" :href="actionResult.url" target="_blank">
            <v-icon left>open_in_new</v-icon>
            {{ actionResult.urlText }}
          </v-btn>
        </template>
      </modal-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { format } from 'date-fns';

export default {
  name: 'ReportActions',

  props: {
    report: {
      type: Object,
      default: () => ({}),
    },
    selectedRows: {
      type: Array,
      default: () => [],
    },
    params: {
      type: Object,
      default: () => ({}),
    },
    sharePid: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      loading: false,
      actionDialog: false,
      activeActionKey: '',
      activeAction: {},
      fieldValues: {},
      resultDialog: false,
      actionResult: {},
    };
  },

  computed: {
    actions() {
      return this.report?.actions || {};
    },
    hasActions() {
      return Object.keys(this.actions).length > 0;
    },
    selectedCount() {
      return this.selectedRows.length;
    },
    menuDisabled() {
      return !this.selectedRows.length;
    },
    rowKey() {
      return this.report?.row_key || 'id';
    },
    activeActionFields() {
      const fields = this.activeAction?.fields;
      return fields && Object.keys(fields).length ? fields : null;
    },
    actionFieldsValid() {
      if (!this.activeActionFields) return true;
      return Object.entries(this.activeActionFields).every(([key, field]) => {
        if (!field.required) return true;
        const value = this.fieldValues[key];
        return value !== undefined && value !== null && value !== '';
      });
    },
  },

  methods: {
    ...mapActions('reports', ['runAction', 'runSharedAction']),

    exportTimestamp() {
      return format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
    },

    resolveFieldDefault(key, field) {
      const base = field.default || '';
      if (key === 'file_name') {
        return `${base || 'Export Report'} ${this.exportTimestamp()}`;
      }
      return base;
    },

    handleAction(actionKey, action) {
      this.activeActionKey = actionKey;
      this.activeAction = action;

      // Initialize field values with defaults
      this.fieldValues = {};
      if (action.fields) {
        Object.entries(action.fields).forEach(([key, field]) => {
          this.fieldValues[key] = this.resolveFieldDefault(key, field);
        });
      }

      // If action has fields or needs confirmation, show dialog
      if (action.fields || action.confirm) {
        this.actionDialog = true;
        return;
      }

      // No fields or confirmation needed — execute immediately
      this.executeAction();
    },

    resultFromResponse(
      { message, url, url_text } = {},
      fallbackMessage,
      success
    ) {
      return {
        success,
        message: message || fallbackMessage,
        url: url || '',
        urlText: url_text || 'Open',
      };
    },

    async executeAction() {
      this.loading = true;
      this.actionDialog = false;
      const rowKeys = this.selectedRows.map((row) => row[this.rowKey]);

      try {
        const actionParams = { ...this.params, ...this.fieldValues };
        const actionPayload = {
          reportId: this.report.id,
          actionKey: this.activeActionKey,
          rowKeys,
          params: actionParams,
        };

        let result;
        if (this.sharePid) {
          result = await this.runSharedAction({
            sharePid: this.sharePid,
            ...actionPayload,
          });
        } else {
          result = await this.runAction(actionPayload);
        }
        this.actionResult = this.resultFromResponse(
          result,
          'Action completed',
          true
        );
        this.$emit('action-complete');
      } catch (e) {
        this.actionResult = this.resultFromResponse(
          e?.response?.data,
          'Action failed',
          false
        );
      } finally {
        this.loading = false;
        this.resultDialog = true;
      }
    },
  },
};
</script>
